"use client";

import { useEffect, useMemo, useState } from "react";
import { useConfigurator } from "@/store/configurator";

export interface PriceQuote {
  quoteId: string;
  basePrice: number;
  unitPrice: number;
  totalPrice: number;
  discountPct: number;
  surcharges: { key: string; name: string; amount: number }[];
  /** Configured amounts for options the customer has not switched on yet. */
  availableSurcharges: { key: string; name: string; amount: number }[];
  /** Quantity choices and discounts returned by the pricing table. */
  quantityTiers: { minQuantity: number; multiplier: number; label: string | null }[];
}

export type PriceStatus = "idle" | "loading" | "ready" | "error";

const UNAVAILABLE = "Pricing is unavailable right now. Please try again.";

/**
 * Classify a non-OK response from our own price endpoint.
 *
 * A 4xx carrying an explanation is a VALIDATED REFUSAL: the customer chose
 * something we hold no approved specification for, and the endpoint is working
 * exactly as designed. Anything else — a 5xx, an unparseable body, an
 * unexplained 4xx — is a genuine fault and is still reported.
 *
 * Extracted so the distinction is testable without rendering the hook.
 */
export function classifyPriceFailure(
  status: number,
  data: unknown,
): { refusal: boolean; message: string } {
  const error = (data as { error?: unknown } | null)?.error;
  const refusal = status >= 400 && status < 500 && typeof error === "string" && error.trim().length > 0;
  return { refusal, message: refusal ? (error as string) : UNAVAILABLE };
}

export interface PriceResult {
  quote: PriceQuote | null;
  status: PriceStatus;
  /**
   * Why pricing is unavailable, in the customer's words. Set for a validated
   * refusal from our own endpoint ("this gauge is not an approved
   * specification"); a generic line for an unexpected failure.
   */
  message: string | null;
}

/**
 * Live price for the current configuration. Debounced, cancels stale
 * requests, and stays "idle" until enough is selected to price (line chosen).
 * Shared by the persistent footer and step-level surcharge notes.
 *
 * ponytail: each caller fetches independently (2-3 debounced requests per
 * change); lift into a context provider if the endpoint ever gets expensive.
 */
export function useConfiguratorPrice(): PriceResult {
  const { state } = useConfigurator();

  const {
    instrumentId, lineId, stringTypeId, stringCount, quantity,
    isCustomGauge, isPerStringColor, isNonStandard, scaleLengthId, colorId, presetId, strings,
  } = state;

  // Derived, not stored. "Not enough selected to price yet" and "the answer we
  // hold is for an older selection" are both properties of the current
  // selection, so neither needs a state write inside the effect — which also
  // keeps the effect free of the cascading synchronous setState the React
  // lint rule warns about.
  const request = useMemo(() => (
    instrumentId && lineId && stringCount && colorId && scaleLengthId && strings.length === stringCount
      ? {
          instrumentId, lineId, stringTypeId, stringCount, quantity,
          isCustomGauge, isPerStringColor, isNonStandard,
          scaleLengthId, colorId, presetId, strings,
        }
      : null
  ), [
    instrumentId, lineId, stringTypeId, stringCount, quantity,
    isCustomGauge, isPerStringColor, isNonStandard,
    scaleLengthId, colorId, presetId, strings,
  ]);
  const key = request ? JSON.stringify(request) : null;

  // Answers are stored with the key they were computed for, so an answer for a
  // superseded configuration is never shown as this one's price.
  const [answer, setAnswer] = useState<(PriceResult & { key: string }) | null>(null);

  useEffect(() => {
    if (!request || !key) return;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch("/api/configurator/price", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify(request),
        });
        const data = await response.json().catch(() => null);
        if (controller.signal.aborted) return;

        if (!response.ok) {
          // Refusals are shown, not logged as defects — see
          // classifyPriceFailure. Reporting them through console.error raised
          // the dev overlay's red error indicator during ordinary use.
          const { refusal, message } = classifyPriceFailure(response.status, data);
          if (!refusal) console.error("Live price lookup failed:", response.status, data);
          setAnswer({ key, quote: null, status: "error", message });
          return;
        }

        setAnswer({
          key, status: "ready", message: null,
          quote: {
            quoteId: data.quoteId,
            basePrice: data.basePrice,
            unitPrice: data.unitPrice,
            totalPrice: data.totalPrice,
            discountPct: data.discountPct,
            surcharges: data.surcharges ?? [],
            availableSurcharges: data.availableSurcharges ?? [],
            quantityTiers: data.quantityTiers ?? [],
          },
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        // Transport failure, not a decision from the endpoint. Still a fault.
        console.error("Live price lookup failed:", err);
        setAnswer({ key, quote: null, status: "error", message: UNAVAILABLE });
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [request, key]);

  if (!key) return { quote: null, status: "idle", message: null };
  if (!answer || answer.key !== key) return { quote: null, status: "loading", message: null };
  return { quote: answer.quote, status: answer.status, message: answer.message };
}
